import { createContext, useContext, useState, useEffect } from 'react';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  signInWithRedirect,
  confirmSignIn,
  updateUserAttributes
} from 'aws-amplify/auth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [role, setRole] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Verificar sesión actual al cargar la aplicación
  useEffect(() => {
    checkUser();
  }, []);

  /**
   * Verificar si hay una sesión activa
   */
  const checkUser = async () => {
    try {
      setLoading(true);
      setAuthError(null); // Limpiar errores previos

      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const session = await fetchAuthSession();

      // Extraer grupos del token de acceso
      const accessToken = session.tokens?.accessToken;
      const userGroups = accessToken?.payload['cognito:groups'] || [];

      // Extraer role del custom attribute
      const userRole = attributes['custom:role'] || null;

      // Extraer locations del custom attribute
      let userLocations = [];
      if (attributes['custom:locations']) {
        try {
          // Parsear el custom attribute (puede ser JSON string o array)
          const locationsAttr = attributes['custom:locations'];
          userLocations = typeof locationsAttr === 'string'
            ? JSON.parse(locationsAttr)
            : locationsAttr;
        } catch (error) {
          console.error('Error parsing locations attribute:', error);
          userLocations = [];
        }
      }

      setUser({
        username: currentUser.username,
        email: attributes.email,
        name: attributes.given_name || attributes.email,
        attributes,
      });
      setGroups(userGroups);
      setRole(userRole);
      setLocations(userLocations);
    } catch (error) {
      console.log('No user logged in:', error);
      setUser(null);
      setGroups([]);
      setRole(null);
      setLocations([]);

      // Capturar errores de OAuth (cuando Google redirige de vuelta)
      if (error.name === 'OAuthError' || error.message?.includes('oauth')) {
        setAuthError('Error al autenticar con Google. Por favor intenta de nuevo.');
      } else if (error.name === 'UserNotFoundException') {
        setAuthError('Usuario no encontrado. Contacta al administrador.');
      } else if (error.name === 'NotAuthorizedException') {
        setAuthError('No tienes permisos para acceder. Contacta al administrador.');
      }
      // No establecer error para casos normales de "no hay sesión"
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login con email y contraseña
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña
   */
  const login = async (email, password) => {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password: password,
      });

      if (isSignedIn) {
        await checkUser();
        return { success: true };
      }

      // Manejar diferentes estados del nextStep
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return {
          success: false,
          requiresPasswordChange: true,
          message: 'Debes cambiar tu contraseña temporal',
        };
      }

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        return {
          success: false,
          requiresMFA: true,
          message: 'Ingresa tu código TOTP',
        };
      }

      return {
        success: false,
        message: 'Se requiere completar el proceso de autenticación',
      };
    } catch (error) {
      console.error('Error logging in:', error);

      // Mapear errores de Cognito a mensajes amigables
      let errorMessage = 'Error al iniciar sesión';

      if (error.name === 'NotAuthorizedException') {
        errorMessage = 'Email o contraseña incorrectos';
      } else if (error.name === 'UserNotFoundException') {
        errorMessage = 'Usuario no encontrado';
      } else if (error.name === 'UserNotConfirmedException') {
        errorMessage = 'Usuario no confirmado. Verifica tu email';
      } else if (error.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  };

  /**
   * Login con Google OAuth
   * Redirige a Google para autenticación
   */
  const signInWithGoogle = async () => {
    try {
      // Si ya hay una sesión activa, no hacer nada (el LoginPage redirigirá automáticamente)
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          // Ya hay un usuario autenticado, actualizar estado y retornar
          await checkUser();
          return; // Salir sin error
        }
      } catch (err) {
        // No hay usuario autenticado, continuar con el login
      }

      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  /**
   * Cerrar sesión
   */
  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setGroups([]);
      setRole(null);
      setLocations([]);
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  };

  /**
   * Obtener el token de acceso JWT
   * @returns {string|null} Token de acceso o null
   */
  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  };

  /**
   * Obtener el token de ID JWT
   * @returns {string|null} Token de ID o null
   */
  const getIdToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  };

  /**
   * Verificar si el usuario pertenece a un grupo específico
   * @param {string} groupName - Nombre del grupo
   * @returns {boolean}
   */
  const isInGroup = (groupName) => {
    return groups.includes(groupName);
  };

  /**
   * Verificar si el usuario es administrador
   * @returns {boolean}
   */
  const isAdmin = () => {
    return isInGroup('Admin');
  };

  /**
   * Verificar si el usuario es admin operacional
   * @returns {boolean}
   */
  const isOperationalAdmin = () => {
    return isInGroup('OperationalAdmin') ||
           isInGroup('OperationalAdminRC') ||
           isInGroup('OperationalAdminEX');
  };

  /**
   * Verificar si el usuario es supervisor
   * @returns {boolean}
   */
  const isSupervisor = () => {
    return isInGroup('Supervisor') ||
           isInGroup('SupervisorRC') ||
           isInGroup('SupervisorEX');
  };

  /**
   * Confirmar nueva contraseña para usuario con contraseña temporal
   * @param {string} newPassword - Nueva contraseña
   * @param {object} userAttributes - Atributos del usuario (nombre, apellido)
   */
  const confirmNewPassword = async (newPassword, userAttributes = {}) => {
    try {
      // Confirmar el nuevo password
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes: userAttributes
        }
      });

      if (isSignedIn) {
        // Si se proporcionaron atributos adicionales, actualizarlos
        if (Object.keys(userAttributes).length > 0) {
          try {
            await updateUserAttributes({
              userAttributes: userAttributes
            });
          } catch (error) {
            console.error('Error updating user attributes:', error);
            // No fallar si solo falla la actualización de atributos
          }
        }

        await checkUser();
        return { success: true };
      }

      return {
        success: false,
        message: 'No se pudo completar el cambio de contraseña',
      };
    } catch (error) {
      console.error('Error confirming new password:', error);

      let errorMessage = 'Error al cambiar la contraseña';

      if (error.name === 'InvalidPasswordException') {
        errorMessage = 'La contraseña no cumple con los requisitos de seguridad';
      } else if (error.name === 'LimitExceededException') {
        errorMessage = 'Demasiados intentos. Intenta más tarde';
      } else if (error.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  };

  /**
   * Limpiar el error de autenticación
   */
  const clearAuthError = () => {
    setAuthError(null);
  };

  const value = {
    user,
    loading,
    groups,
    role,
    locations,
    authError,
    login,
    signInWithGoogle,
    logout,
    checkUser,
    confirmNewPassword,
    clearAuthError,
    getAccessToken,
    getIdToken,
    isInGroup,
    isAdmin,
    isOperationalAdmin,
    isSupervisor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook personalizado para acceder al contexto de autenticación
 * @returns {object} Contexto de autenticación
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
